import type { Locale } from '@/i18n/locales';
import type { TargetDomainEntry } from '@/lib/types/tenant';

import { findMatchingDomainEntry, getHostname } from './domain';

type LocalizedTargetDomainEntry = { pattern: string; locale: Locale };

function isLocalizedEntry(entry: TargetDomainEntry): entry is LocalizedTargetDomainEntry {
  return typeof entry !== 'string' && !!entry.locale;
}

/**
 * Resolves the initial locale for the widget based on the parent page's host
 * and the tenant's `iframeNavigation.targetDomains` entries. First matching
 * entry with a `locale` wins — list more specific patterns first.
 *
 * Returns null when no entry matches or when the matched entry has no locale.
 */
export function resolveHostLocale({
  parentUrl,
  targetDomains,
}: {
  parentUrl: string | null | undefined;
  targetDomains: readonly TargetDomainEntry[] | null | undefined;
}): Locale | null {
  if (!parentUrl || !targetDomains?.length) return null;

  const hostname = getHostname(parentUrl);
  if (!hostname) return null;

  const localizedEntries = targetDomains.filter(isLocalizedEntry);
  const match = findMatchingDomainEntry({ hostname, entries: localizedEntries });
  if (!match || typeof match === 'string') return null;
  return match.locale ?? null;
}

export function hasLocalizedTargetDomains(
  targetDomains: readonly TargetDomainEntry[] | null | undefined,
): boolean {
  if (!targetDomains?.length) return false;
  return targetDomains.some(isLocalizedEntry);
}
